import { DialogMessenger } from "./dialogMessenger";
import { OfficeDialogLocation } from "./messengerFactory";

/**
 * Some notes for futurue refactorings...
 *
 * DialogMessenger interface does not really makes sense.
 * setData is only called on host, while getData is only called from within the dialog
 * callAsyncMethod is only called from dialog...
 *
 * So it would make much more sense to think about send (setData) and receive (getData) but... yeah :D
 *
 * SetData/GetData needs to support events though as it is called when the dialog is not necessarily open.
 * So we need to fetch the data when the dialog opens.
 */
export const OfficeEventDialogMessengerName = 'OfficeEventDialogMessenger';
export class OfficeEventDialogMessenger extends DialogMessenger {
    private asyncCallbacks: {
        [key: string]: (result: Office.AsyncResult<any> | string) => void;
    } = {};

    private hostData: { [key: string]: string } = {};
    private dialog: Office.Dialog | null = null;
    private proxyCounter = 0;

    constructor(location: OfficeDialogLocation) {
        super();
        //Within dialog, we need to catch events from the Host application...
        if (location === 'dialog') {
            Office.context.ui.addHandlerAsync(Office.EventType.DialogParentMessageReceived, (arg: { message: string }) => {
                try {
                    let message: { key: string; type: 'callAsync' | 'getData'; value?: string; } = JSON.parse(arg.message);
                    if (this.asyncCallbacks[message.key] && message.type === 'callAsync') {
                        this.handleCallback(message.key, message.value);
                    } else if (this.asyncCallbacks[message.key] && message.type === 'getData') {
                        let resolveFct = this.asyncCallbacks[message.key];
                        if (resolveFct && message.value) {
                            resolveFct(message.value);
                            delete this.asyncCallbacks[message.key];
                        }
                    }
                } catch (e) {
                    //not for us
                }
            });
        }
    }

    setDialog(dialog: Office.Dialog) {
        //On the host application, the proxy is initialized before the dialog has been opened...
        //Add dialog afterwards for event handling
        this.dialog = dialog;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: { message: string; origin: string }) => {
            try {
                let message: { key: string; type: 'getData'; value?: string; resolverId: string } = JSON.parse(arg.message);
                if (message.type === 'getData' && this.hostData[message.key]) {
                    let messageData = this.hostData[message.key];
                    this.dialog.messageChild(JSON.stringify({ type: 'getData', key: message.resolverId, value: messageData }));
                }
            } catch (e) {
                //not for us
            }
        });
    }

    private handleCallback(key: string, newValue: string) {
        //Deserialize & execute callback method
        let data = JSON.parse(newValue).args;
        if (this.asyncCallbacks[key] && typeof (this.asyncCallbacks[key]) === 'function')
            this.asyncCallbacks[key].apply(this, data);
        //Clear callback & storage of result
        delete this.asyncCallbacks[key];
    }

    setData(key: string, message: string) {
        //Set Data only stores the data in the host app
        this.hostData[key] = message;
        try {
            let parsedMessage = JSON.parse(message);
            if (parsedMessage.args) {
                this.dialog.messageChild(JSON.stringify({ type: 'callAsync', key: key, value: message }));
            }
        } catch (e) { console.log(e); }
    }

    async getData(key: string): Promise<string> {
        //Get Data throws an event to the host, get the result in the constructor event handler and resolves this promise.
        let result = await new Promise<string>((resolve, reject) => {
            let resolverId = "dialog-rid-" + new Date().getTime().toString() + "-" + this.proxyCounter++;
            this.asyncCallbacks[resolverId] = resolve as any;
            /** If a user switches the mail in outlook/outlook online during the dialog open event, the connection to the parent window short cuts and is not fixable.
             * This leads to the promise never resolving. This is not repairable from our code. If this happens with the dev app, even the prod app does break!
             * This is related to the window objects on the top window not being cleared correctly.
             * The only way to resolve this is to restart outlook or refresh the browser window.
             * So after a certain time of waiting, we reject the promise to prevent it from hanging indefinitely and let the user know what happened and how to resolve it.
             */
            window.setTimeout(() => {
                if (this.asyncCallbacks[resolverId]) {
                    delete this.asyncCallbacks[resolverId];
                    reject(new Error("The dialog timed out. If this persists try restarting Outlook."));
                }

            }, 30 * 1000); //Timeout after 30 seconds
            Office.context.ui.messageParent(JSON.stringify({ type: 'getData', key: key, resolverId: resolverId }));
        });
        return result;
    }

    callAsyncMethod(message: any, cbk?: (data: any) => void) {
        let cbkId = null;
        if (cbk && typeof (cbk) === 'function') {
            cbkId = "dialog-cbk-" + new Date().getTime().toString() + "-" + this.proxyCounter++;
            this.asyncCallbacks[cbkId] = cbk;
        }
        message.callbackId = cbkId;
        message.type = 'callAsync';
        Office.context.ui.messageParent(JSON.stringify(message));
    }

    getName(): string {
        return OfficeEventDialogMessengerName;
    }

    cleanup() {
        this.hostData = {};
        this.asyncCallbacks = {};
    }

}
